/**
 * COBBLESTONE (2026-10-19, Matt: "Add cobblestone in squares and rectangles as pavement
 * materials that can be applied and give us colors to choose from. Give us stacked and running
 * bonds. Give them standard borders."): four floor finishes painted on like any other — granite
 * setts laid as real pieces through the FINISH LAYOUT engine (stack / running), each with its
 * standard border around the drawn outline:
 *  - Square, 4" x 4": a double row of the same setts around the edge
 *  - Rectangle, 4" x 7" (Belgian block): a single soldier course, setts on end to the edge
 * 3" deep over the slab, 3/8" sand-swept joints. The stone's color is picked with the Paint
 * Brush from the granite colors below (factory colors of the cobblestone line — finishCompat).
 */
import type { ModelDoc } from './doc'
import { listMaterials } from './doc'
import { createMaterial, updateMaterial } from './commands/library'
import { getColor, putColors, type ColorRecord } from './colors'
import type { FinishLayoutSpec } from './types'

export const COBBLESTONE_LINE = 'granite-cobblestone'

const IN = 1 / 12
const SAND = '#b9ae98'
const BASE = { jointFt: 0.375 * IN, jointDepthFt: 0.5 * IN, groutColor: SAND, shades: 2 }

export const COBBLESTONE_MATERIALS: { name: string; layout: FinishLayoutSpec }[] = [
  { name: 'Cobblestone — Square, Stacked Bond', layout: { ...BASE, pattern: 'stack', widthFt: 4 * IN, lengthFt: 4 * IN, borderCourses: 2, borderStyle: 'sailor' } },
  { name: 'Cobblestone — Square, Running Bond', layout: { ...BASE, pattern: 'running', widthFt: 4 * IN, lengthFt: 4 * IN, borderCourses: 2, borderStyle: 'sailor' } },
  { name: 'Cobblestone — Rectangle, Stacked Bond', layout: { ...BASE, pattern: 'stack', widthFt: 4 * IN, lengthFt: 7 * IN, borderCourses: 1, borderStyle: 'soldier' } },
  { name: 'Cobblestone — Rectangle, Running Bond', layout: { ...BASE, pattern: 'running', widthFt: 4 * IN, lengthFt: 7 * IN, borderCourses: 1, borderStyle: 'soldier' } },
]

/** the granite colors the setts come in (factory colors of the cobblestone line) */
export const COBBLESTONE_COLORS: ColorRecord[] = [
  { id: 'cobble-silver-grey', name: 'Silver Grey Granite', hex: '#9b9c9b' },
  { id: 'cobble-dark-grey', name: 'Dark Grey Granite', hex: '#6c6e71' },
  { id: 'cobble-charcoal', name: 'Charcoal Basalt', hex: '#4a4b4e' },
  { id: 'cobble-bluestone', name: 'Bluestone', hex: '#66727a' },
  { id: 'cobble-pink', name: 'Rose Granite', hex: '#b39a90' },
  { id: 'cobble-gold', name: 'Golden Granite', hex: '#b8a27c' },
  { id: 'cobble-porphyry', name: 'Porphyry Red', hex: '#7b4b40' },
].map((c) => ({ ...c, kind: 'factory' as const, maker: 'Cobblestone', lines: [COBBLESTONE_LINE] }))

/** the four finishes + their colors, added the first time a model opens (idempotent; never
 *  overwrites a finish or color someone has since edited). The default library seeds the
 *  finishes only (`withColors` false) — the colors arrive when the model opens. -> how many added */
export function ensureCobblestoneLibrary(model: ModelDoc, withColors = true): number {
  let n = 0
  const mats = listMaterials(model)
  for (const def of COBBLESTONE_MATERIALS) {
    if (mats.some((m) => m.name === def.name)) continue
    const id = createMaterial(model, {
      name: def.name, color: COBBLESTONE_COLORS[0].hex, category: 'Paving',
      cut: { pattern: 'pavers', fgColor: '#5e6062', bgColor: '#ecebe8' },
    })
    updateMaterial(model, id, {
      floorFinish: { thicknessFt: 3 * IN },
      floorLayout: { ...def.layout },
      substrate: 'pavers',
      productLine: COBBLESTONE_LINE,
    })
    n++
  }
  if (!withColors) return n
  const missing = COBBLESTONE_COLORS.filter((c) => !getColor(model, c.id))
  if (missing.length) n += putColors(model, missing)
  return n
}
